import { type Editor, Extension } from "@tiptap/core";
import type { Node as NodoPM } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

/**
 * Fase 4 · las marcas del revisor en el editor del autor (pantalla 05 tras una devolución). Son
 * decoraciones de ProseMirror, no parte del contenido: no cambian lo que se firma. Nacen donde el
 * servidor dice que está cada frase (`lugar`, sobre el texto de su bloque) y después las mueve
 * ProseMirror con cada cambio: si el autor escribe antes de la frase, la marca la sigue; si borra
 * la frase entera, la marca desaparece del texto (sigue en el panel).
 */

export type MarcaEnEditor = {
  numero: number;
  tipo: "corregir" | "sugerencia" | "comentario";
  bloqueId: string;
  inicio: number;
  fin: number;
  titulo: string;
  /** Un tramo más de la misma marca (varios párrafos): sin el número delante. */
  sinNumero?: boolean;
};

const claveMarcas = new PluginKey<DecorationSet>("marcasDeRevision");

export const MarcasDeRevision = Extension.create({
  name: "marcasDeRevision",
  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: claveMarcas,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, conjunto) {
            const nuevo = tr.getMeta(claveMarcas) as DecorationSet | undefined;
            if (nuevo) return nuevo;
            if (!tr.docChanged) return conjunto;
            const movido = conjunto.map(tr.mapping, tr.doc);
            // Si se borró la frase entera, su resaltado desaparece al mapear; su número (un
            // widget) no, y quedaría suelto: se quita también.
            const conTexto = new Set(
              movido
                .find(undefined, undefined, () => true)
                .filter((d) => d.to > d.from)
                .map((d) => d.spec.numero as number),
            );
            const sueltos = movido.find(
              undefined,
              undefined,
              (spec) => !conTexto.has(spec.numero as number),
            );
            return sueltos.length ? movido.remove(sueltos) : movido;
          },
        },
        props: {
          decorations: (estado) => claveMarcas.getState(estado),
        },
      }),
    ];
  },
});

/**
 * Pone en el editor las marcas abiertas que falten y quita las que ya no están abiertas. Las que
 * ya estaban se quedan donde ProseMirror las llevó (el `lugar` del servidor es del último
 * guardado; el editor puede ir adelante).
 */
export function sincronizarMarcas(editor: Editor, abiertas: MarcaEnEditor[]): void {
  const actual = claveMarcas.getState(editor.state) ?? DecorationSet.empty;
  const numeros = new Set(abiertas.map((m) => m.numero));
  const sobran = actual.find(undefined, undefined, (spec) => !numeros.has(spec.numero as number));
  const tiene = new Set(actual.find().map((d) => d.spec.numero as number));
  const nuevas = abiertas.flatMap((m) => {
    if (tiene.has(m.numero)) return [];
    const r = rangoDe(editor.state.doc, m.bloqueId, m.inicio, m.fin);
    return r
      ? [
          Decoration.inline(
            r.desde,
            r.hasta,
            {
              class: "marca-revision",
              "data-tipo": m.tipo,
              "data-numero": String(m.numero),
              title: m.titulo,
            },
            { numero: m.numero },
          ),
          // El número, una sola vez, al principio de la frase.
          ...(m.sinNumero
            ? []
            : [
                Decoration.widget(r.desde, () => numeroDeMarca(m), {
                  numero: m.numero,
                  side: -1,
                  ignoreSelection: true,
                }),
              ]),
        ]
      : [];
  });
  if (!sobran.length && !nuevas.length) return;
  const conjunto = actual.remove(sobran).add(editor.state.doc, nuevas);
  editor.view.dispatch(
    editor.state.tr.setMeta(claveMarcas, conjunto).setMeta("addToHistory", false),
  );
}

/** Dónde está hoy la marca en el editor: lleva el cursor ahí y la muestra. */
export function irAMarca(editor: Editor, numero: number): boolean {
  const conjunto = claveMarcas.getState(editor.state);
  const deco = conjunto
    ?.find(undefined, undefined, (spec) => spec.numero === numero)
    .find((d) => d.to > d.from);
  if (!deco) return false;
  editor.chain().focus().setTextSelection({ from: deco.from, to: deco.to }).scrollIntoView().run();
  return true;
}

function numeroDeMarca(m: MarcaEnEditor): HTMLElement {
  const n = document.createElement("span");
  n.className = "marca-revision-n";
  n.dataset.tipo = m.tipo;
  n.textContent = String(m.numero);
  n.contentEditable = "false";
  return n;
}

/**
 * De desplazamientos sobre el texto de un bloque (`textoDe` de shared: el texto pegado, y una
 * referencia cuenta como su código) a posiciones de ProseMirror, donde una referencia es un
 * átomo de tamaño 1. Una marca que empieza o termina dentro de una referencia la toma entera.
 */
export function rangoDe(
  doc: NodoPM,
  bloqueId: string,
  inicio: number,
  fin: number,
): { desde: number; hasta: number } | null {
  let rango: { desde: number; hasta: number } | null = null;
  doc.descendants((nodo, pos) => {
    if (rango) return false;
    if (nodo.attrs.id !== bloqueId) return true;
    if (!nodo.isTextblock) return false;
    // antesDe[t]: la posición del carácter t; despuesDe[t]: la posición tras el carácter t.
    const antesDe: number[] = [];
    const despuesDe: number[] = [];
    nodo.forEach((hijo, desplazamiento) => {
      const p = pos + 1 + desplazamiento;
      if (hijo.isText) {
        for (let k = 0; k < (hijo.text?.length ?? 0); k++) {
          antesDe.push(p + k);
          despuesDe.push(p + k + 1);
        }
      } else if (hijo.type.name === "referencia") {
        for (let k = 0; k < String(hijo.attrs.codigo ?? "").length; k++) {
          antesDe.push(p);
          despuesDe.push(p + 1);
        }
      }
    });
    const desde = antesDe[inicio];
    const hasta = despuesDe[fin - 1];
    if (desde !== undefined && hasta !== undefined && hasta > desde) rango = { desde, hasta };
    return false;
  });
  return rango;
}
